import { HomePageSpecialBanner } from "@/api/generated/BMWInterfaces"
import { Desktop, Mobile } from "@/lib/common/MediaQueries"
import colors from "@/lib/theme/colors"
import styled from "@emotion/styled"
import { Box } from "@mui/material"
import { useLottie } from "lottie-react"
import Image from "next/image"
import { useState } from "react"
import PinIcon from "../shared/icons/PinIcon"
import * as animationData from "../shared/LottieAnimations/Confetti.json"
import BannerCounter from "./BannerCounter"

const StyledLayout = styled.div`
  background: ${colors.directAzure};
  width: 100%;
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  gap: 30px;
  justify-content: space-between;
  overflow: hidden;
  position: relative;
  max-width: calc(1920px - 100px - 100px - 80px);
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 620px;
    width: calc(100% - 80px);
    margin: 0 auto;
    height: 475px;
    border-radius: 24px;
    gap: 32px;
    box-shadow: 0px 4px 16px 0px #00000042;
    flex-direction: row;
    gap: 32px;
  }
`

const StyledInfoWrapper = styled.div`
  width: 100%;
  max-width: 510px;
  padding: 56px 16px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 32px 0 32px 32px;
    align-items: flex-start;
  }
`
const StyledImageSideWrapper = styled.div`
  width: 100%;
  height: fit-content;
  position: relative;
  padding: 0 16px 56px;
  display: flex;
  flex-direction: column;
  align-items: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0 32px 0 0;
    height: 100%;
    display: block;
  }
`

const StyledBackground = styled(Image)`
  position: absolute;
  width: 100%;
  height: 268px;
  bottom: 0;
  right: -50%;
  transform: translateX(-50%);
  object-position: top;
  ${(props) => props.theme.breakpoints.up("md")} {
    right: 0;
    top: 50%;
    bottom: unset;
    transform: translateY(-50%);
    height: 100%;
    width: 100%;
    max-width: 85%;
    object-position: left;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    min-width: 564px;
  }
  object-fit: cover;
`

const StyledImageWrapper = styled.div`
  position: relative;
  width: 328px;
  height: 169px;
  margin: 0 auto;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 100%;
    height: 100%;
    margin: 0;
  }
`

const StyledBannerTitle = styled.div`
  font-weight: 400;
  font-size: 24px;
  line-height: 35px;
  letter-spacing: 0;
  text-align: center;
  p {
    margin: 0;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 40px;
    text-align: right;
    line-height: 59px;
  }
`

const StyledLocation = styled.div`
  display: inline-flex;
  gap: 4px;
  align-items: center;
  margin-top: 16px;
`
const StyledLocationText = styled.div`
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  p {
    margin: 0;
  }
`

const StyledBannerDescription = styled.div`
  padding: 8px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.4);
  font-weight: 400;
  font-size: 18px;
  line-height: normal;
  letter-spacing: 0;
  text-align: center;
  margin-bottom: 24px;
  position: relative;
  z-index: 2;
  flex-grow: 1;
  .Price {
    font-weight: 700;
  }
  p {
    margin: 0;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 16px;
    text-align: right;
    font-size: 18px;
    line-height: normal;
    margin-bottom: 32px;
  }
`

const StyledBottomText = styled.div`
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-align: center;
  font-size: 12px;
  display: block;
  position: relative;
  z-index: 2;
  align-self: center;
  max-width: 250px;
  margin: auto;
  p {
    margin: 0;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    max-width: unset;
    text-align: right;
    font-size: 14px;
  }
`

const StyledAnimation = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9;
`

export default function SpecialBanner({
  data,
}: {
  data: HomePageSpecialBanner
}): React.ReactElement {
  const [isAnimationVisible, setIsAnimationVisible] = useState(true)
  const defaultOptions = {
    animationData: animationData,
    loop: false,
    onComplete: () => {
      setIsAnimationVisible(false)
    },
  }
  const { View } = useLottie(defaultOptions)

  return (
    <StyledLayout>
      <StyledAnimation>
        {isAnimationVisible && <StyledAnimation>{View}</StyledAnimation>}
      </StyledAnimation>
      <StyledImageSideWrapper>
        <Desktop>
          <StyledBackground
            src={"/assets/banner-background.svg"}
            alt={"background"}
            width={564}
            height={475}
            objectFit="cover"
          />
        </Desktop>
        <Mobile>
          <StyledBackground
            src={"/assets/banner-background-mobile.svg"}
            alt={"background"}
            width={564}
            height={475}
            objectFit="cover"
          />
        </Mobile>
        <StyledImageWrapper>
          <Image
            src={data?.media?.imageUrl}
            alt={data?.media?.alt || ""}
            fill={true}
            objectFit="contain"
          />
        </StyledImageWrapper>
        <Mobile>
          <Box marginTop={"16px"}>
            <StyledBannerDescription dangerouslySetInnerHTML={{ __html: data?.description }} />
            <StyledBottomText dangerouslySetInnerHTML={{ __html: data?.legalText }} />
          </Box>
        </Mobile>
      </StyledImageSideWrapper>
      <StyledInfoWrapper>
        <StyledBannerTitle dangerouslySetInnerHTML={{ __html: data?.title }} />
        {data?.duoDate && <BannerCounter dueDate={data.duoDate} />}
        {data?.location && (
          <StyledLocation>
            <PinIcon />
            <StyledLocationText dangerouslySetInnerHTML={{ __html: data?.location }} />
          </StyledLocation>
        )}
        <Desktop>
          <Box marginTop={"32px"}>
            <StyledBannerDescription dangerouslySetInnerHTML={{ __html: data?.description }} />
            <StyledBottomText dangerouslySetInnerHTML={{ __html: data?.legalText }} />
          </Box>
        </Desktop>
      </StyledInfoWrapper>
    </StyledLayout>
  )
}
